import React, { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  Send,
  Info,
  Paperclip,
  X,
  CheckCheck,
  Clock
} from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { useListConversationsQuery, createListConversationsKey, ConversationSummary } from '@/client/api/whatsapp/list-conversations.query';
import { useGetConversationQuery, createGetConversationKey, ConversationTimelineItem } from '@/client/api/whatsapp/get-conversation.query';
import { useReplyConversationMutation } from '@/client/api/whatsapp/reply-conversation.mutation';
import { useListNumbersQuery } from '@/client/api/whatsapp/list-numbers.query';
import { useListTemplatesQuery } from '@/client/api/whatsapp/list-templates.query';
import NumberBadge, { useSenderLabel } from './NumberBadge';
import { MultiSelectFilter } from '@/client/components/admin/MultiSelectFilter';
import MessageAttachment, { mediaKindOf } from './MessageAttachment';
import MessageText from '@/client/components/admin/MessageText/MessageText';
import { useSendWithMediaMutation } from '@/client/api/whatsapp/send-with-media.mutation';
import { useMarkConversationReadMutation } from '@/client/api/whatsapp/conversation-actions.mutation';

const statusIconFor = (status: string) => {
  switch (status) {
    case 'read':
      return <CheckCheck className="w-4 h-4 text-blue-500" />;
    case 'delivered':
      return <CheckCheck className="w-4 h-4 text-gray-400" />;
    case 'sent':
      return <CheckCheck className="w-4 h-4 text-gray-300" />;
    default:
      return <Clock className="w-4 h-4 text-gray-300" />;
  }
};

const extractMessageText = (item: ConversationTimelineItem): string => {
  if (item.direction === 'inbound') {
    if (item.message_type === 'text') return item.data?.body || '';
    return `[${item.message_type || 'message'}]`;
  }
  // A template send is stored as Meta's payload: { type: 'template', template: { name } }.
  const template =
    (item.payload?.template as { name?: string } | undefined)?.name || item.payload?.template_name;
  return (
    item.payload?.text?.body ||
    item.payload?.body ||
    (template ? `Template: ${String(template).replace(/_/g, ' ')}` : `[${item.payload?.type || 'message'}]`)
  );
};

/** Two letters for the avatar: from the name when there is one, else the number's last digits. */
const initialsOf = (c: ConversationSummary) =>
  c.name
    ? c.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
    : c.phone.slice(-2);

/** Time alone for today's messages; the date as well for anything older. */
const messageTime = (timestamp: number) => {
  const at = moment.unix(timestamp);
  return at.isSame(moment(), 'day') ? at.format('HH:mm') : at.format('D MMM, HH:mm');
};

const ConversationsPage: React.FC = () => {
  const queryClient = useQueryClient();
  // Poll the list so new inbound conversations surface without a manual refresh.
  // ?phone= opens straight onto one conversation: how the shared inbox hands over.
  const [urlParams] = useSearchParams();
  const linkedPhone = (urlParams.get('phone') || '').replace(/\D/g, '');
  const [selectedPhone, setSelectedPhone] = useState<string | null>(linkedPhone || null);
  const [searchQuery, setSearchQuery] = useState(linkedPhone);
  // Which of our numbers to show conversations for. Empty = all of them;
  // several = conversations on any of the chosen numbers.
  const [numberFilter, setNumberFilter] = useState<string[]>([]);

  const { data, isLoading } = useListConversationsQuery(
    {
      ...(numberFilter.length ? { number: numberFilter.join(',') } : {}),
      // The server searches names as well as numbers, across every conversation.
      ...(searchQuery.trim().length >= 2 ? { search: searchQuery.trim() } : {}),
    },
    { refetchInterval: 30000 },
  );
  const conversations: ConversationSummary[] = data?.data?.conversations || [];
  const [messageText, setMessageText] = useState('');
  // '' = reply from the number this customer last wrote to (the server picks it).
  const [replyFrom, setReplyFrom] = useState('');
  const { data: numbersData } = useListNumbersQuery();
  const senderLabel = useSenderLabel();
  const [replyTemplate, setReplyTemplate] = useState('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const fileSendMutation = useSendWithMediaMutation();
  const { data: templatesData } = useListTemplatesQuery();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const needle = searchQuery.trim().toLowerCase();
  const filteredConversations = conversations.filter(
    (c) => !needle || c.phone.includes(needle.replace(/\D/g, '') || needle) || (c.name || '').toLowerCase().includes(needle),
  );

  // Poll the open thread more aggressively so inbound replies appear live.
  const { data: conversationData } = useGetConversationQuery(
    selectedPhone || '',
    {},
    { refetchInterval: selectedPhone ? 10000 : undefined },
  );
  const timeline: ConversationTimelineItem[] = [...(conversationData?.data?.timeline || [])].reverse();
  const selectedConversation = conversations.find((c) => c.phone === selectedPhone);

  const { mutate: markRead } = useMarkConversationReadMutation();

  // Meta only accepts free text within 24 hours of the customer's last message
  // to that number; after that it takes an approved template. UltraMsg has no
  // such rule. Work out which case this reply is, so the box can say so up
  // front instead of failing on send.
  // Which of our numbers this customer is talking to. The conversation records
  // it; where it does not (older conversations), the latest message they sent
  // says which number they wrote to.
  const conversationNumber =
    selectedConversation?.last_number_id ||
    [...timeline].reverse().find((m) => m.direction === 'inbound' && m.number_id)?.number_id;
  const replySender = senderLabel(replyFrom || conversationNumber);
  const lastInboundToMeta = Math.max(
    0,
    ...timeline
      .filter((m) => m.direction === 'inbound' && senderLabel(m.number_id)?.method === 'Meta')
      .map((m) => m.timestamp),
  );
  // Decided only once the thread has loaded, so the notice does not flash up
  // for a conversation that is in fact still open.
  const windowClosed =
    Boolean(conversationData) &&
    replySender?.method === 'Meta' &&
    Date.now() / 1000 - lastInboundToMeta > 24 * 60 * 60;
  // Only templates that can go out as they are: approved, no variables to fill
  // in and no media header to attach.
  const sendableTemplates = (templatesData?.data?.data || []).filter(
    (t) =>
      t.status === 'APPROVED' &&
      !JSON.stringify(t.components).includes('{{') &&
      !t.components.some((c) => c.type === 'HEADER' && (c as { format?: string }).format !== 'TEXT'),
  );

  // A reply choice belongs to one conversation; do not carry it to the next.
  // Opening a conversation is also what reads it: clear its unread count.
  useEffect(() => {
    setReplyFrom('');
    setReplyTemplate('');
    setReplyFiles([]);
    if (!selectedPhone) return;
    markRead(
      { phone: selectedPhone },
      { onSuccess: () => queryClient.invalidateQueries({ queryKey: createListConversationsKey() }) },
    );
  }, [selectedPhone, markRead, queryClient]);

  // Keep the message list pinned to the newest message.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [timeline.length, selectedPhone]);

  const replyMutation = useReplyConversationMutation();

  const sending = replyMutation.isPending || fileSendMutation.isPending;
  const canSend = windowClosed ? Boolean(replyTemplate) : Boolean(messageText.trim()) || replyFiles.length > 0;

  const handleSendMessage = () => {
    if (!selectedPhone || !canSend || sending) return;
    const done = {
      onSuccess: () => {
        setMessageText('');
        setReplyTemplate('');
        setReplyFiles([]);
        queryClient.invalidateQueries({ queryKey: createGetConversationKey(selectedPhone) });
        queryClient.invalidateQueries({ queryKey: createListConversationsKey() });
      },
      onError: (error: unknown) => {
        toast.error(error instanceof Error ? error.message : 'Failed to send message');
      },
    };
    // The number this reply leaves from: the one picked, else the one this
    // customer last wrote to.
    const from = replyFrom || conversationNumber || '';

    if (!windowClosed && replyFiles.length > 0) {
      const form = new FormData();
      form.append('to', selectedPhone);
      if (messageText.trim()) form.append('message', messageText);
      replyFiles.forEach((file) => form.append('files', file));
      if (from) form.append('from_number', from);
      fileSendMutation.mutate(form, done);
      return;
    }

    const template = sendableTemplates.find((t) => t.name === replyTemplate);
    replyMutation.mutate(
      {
        phone: selectedPhone,
        ...(windowClosed
          ? { type: 'template' as const, template_name: replyTemplate, language_code: template?.language || 'en_US' }
          : { type: 'text' as const, body: messageText }),
        ...(replyFrom ? { from_number: replyFrom } : {}),
      },
      done,
    );
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">WhatsApp Conversations</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your customer conversations</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="w-80 bg-white border-r flex flex-col">
          <div className="p-4 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {(numbersData?.data?.length || 0) > 1 && (
              <div className="mt-3">
                <MultiSelectFilter
                  label="Our numbers"
                  hideLabel
                  searchable
                  capitalize={false}
                  anyLabel="All numbers"
                  triggerClassName="w-full"
                  options={(numbersData?.data || []).map((number) => ({
                    key: number.phone_number_id,
                    label: senderLabel(number.phone_number_id)?.text || number.phone_number_id,
                  }))}
                  value={numberFilter}
                  onChange={setNumberFilter}
                />
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <p className="p-4 text-sm text-gray-500">Loading conversations...</p>
            )}
            {!isLoading && filteredConversations.length === 0 && (
              <p className="p-4 text-sm text-gray-500">No conversations found</p>
            )}
            {filteredConversations.map((conversation) => (
              <div
                key={conversation.phone}
                onClick={() => setSelectedPhone(conversation.phone)}
                className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedPhone === conversation.phone ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                    {initialsOf(conversation)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-900 truncate">{conversation.name || `+${conversation.phone}`}</h3>
                      <span className="text-xs text-gray-500">
                        {conversation.last_message_at ? moment.unix(conversation.last_message_at).fromNow() : ''}
                      </span>
                    </div>
                    {conversation.name && (
                      <p className="text-xs text-gray-500 truncate mb-1">+{conversation.phone}</p>
                    )}
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-gray-600 capitalize">{conversation.status}</p>
                      <NumberBadge id={conversation.last_number_id} showNumber={false} />
                    </div>
                  </div>
                  {conversation.unread_count > 0 && (
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-xs text-white font-semibold">
                      {conversation.unread_count}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 flex flex-col bg-gray-50">
          {selectedConversation ? (
            <>
              <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                    {initialsOf(selectedConversation)}
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900">
                      {selectedConversation.name || `+${selectedConversation.phone}`}
                      {selectedConversation.name && (
                        <span className="ml-2 text-sm font-normal text-gray-500">+{selectedConversation.phone}</span>
                      )}
                    </h2>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-gray-500 capitalize">{selectedConversation.status}</p>
                      <NumberBadge id={conversationNumber} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {timeline.length === 0 && (
                  <p className="text-sm text-gray-500 text-center">No messages yet</p>
                )}
                {timeline.map((message, index) => (
                  <div
                    key={message.wamid || index}
                    className={`flex ${message.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-md px-4 py-2 rounded-lg ${
                        message.direction === 'outbound'
                          ? 'bg-green-500 text-white'
                          : 'bg-white text-gray-900 border border-gray-200'
                      }`}
                    >
                      {mediaKindOf(message) ? (
                        <MessageAttachment item={message} />
                      ) : (
                        <MessageText text={extractMessageText(message)} tone={message.direction === 'outbound' ? 'dark' : 'light'} />
                      )}
                      <div className={`flex items-center gap-1 mt-1 justify-end ${
                        message.direction === 'outbound' ? 'text-white/70' : 'text-gray-500'
                      }`}>
                        <span className="text-xs">
                          {senderLabel(message.number_id)?.text ? `${senderLabel(message.number_id)?.text} · ` : ''}
                          {messageTime(message.timestamp)}
                          {message.triggered_by?.source === 'outside_admin' ? ' · sent outside admin' : ''}
                        </span>
                        {message.direction === 'outbound' && statusIconFor(message.delivery_status || 'sent')}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="bg-white border-t px-6 py-4">
                {windowClosed && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                    This customer last wrote to the Meta number more than 24 hours ago, so Meta only accepts an
                    approved template here. Pick one below, or switch "From" to a linked line (UltraMsg or Evolution) to send free text.
                  </p>
                )}
                {replyFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {replyFiles.map((file, index) => (
                      <span key={`${file.name}-${index}`} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs text-gray-700">
                        {file.name}
                        <button
                          aria-label={`Remove ${file.name}`}
                          onClick={() => setReplyFiles(replyFiles.filter((_, i) => i !== index))}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3">
                  {!windowClosed && (
                    <label title="Attach files" className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
                      <Paperclip className="w-5 h-5" />
                      <input
                        type="file"
                        multiple
                        hidden
                        onChange={(e) => {
                          setReplyFiles([...replyFiles, ...Array.from(e.target.files || [])]);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                  <select
                    value={replyFrom}
                    onChange={(e) => setReplyFrom(e.target.value)}
                    aria-label="Reply from"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">
                      From: {senderLabel(conversationNumber)?.text || 'default number'}
                    </option>
                    {numbersData?.data?.map((number) => (
                      <option key={number.phone_number_id} value={number.alias || number.phone_number_id}>
                        {senderLabel(number.phone_number_id)?.text}
                      </option>
                    ))}
                  </select>
                  {windowClosed ? (
                    <select
                      value={replyTemplate}
                      onChange={(e) => setReplyTemplate(e.target.value)}
                      aria-label="Template"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">
                        {sendableTemplates.length ? 'Choose a template...' : 'No ready-to-send template is approved'}
                      </option>
                      {sendableTemplates.map((t) => (
                        <option key={t.id} value={t.name}>
                          {t.name.replace(/_/g, ' ')} ({t.language})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <textarea
                      // Enter sends; Shift+Enter starts a new line, as in WhatsApp.
                      rows={Math.min(5, Math.max(1, messageText.split('\n').length))}
                      placeholder="Type a message...  *bold*  _italic_  ~strike~  `code`  (Shift+Enter for a new line)"
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  )}
                  <button
                    onClick={handleSendMessage}
                    disabled={sending || !canSend}
                    className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Info className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No conversation selected</h3>
                <p className="text-sm text-gray-500">Select a conversation to start messaging</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationsPage;
